import { LoadingButton } from "@mui/lab";
import { Box, Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { supportTicketSchema } from "./SupportTicketValidation";
import { FieldValues, useForm } from "react-hook-form";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Support } from "../../../Component/Model/Dto/Support/SupportRequest";
import { CustomDialogTitle } from "../Custom/CustomDialogTitle";
import { SupportRequestService } from "../../../Component/Api/SupportRequest/SupportRequestService";
import { toast } from "react-toastify";
import AppSelectList, { IDropDownOptions } from "../../../Component/Common/Components/FormValidationInputs/AppSelectList";
import { ticketStatusList, ticketTypeList } from "../../../Component/Common/Constants/Lists/DropdownListOptions";
import { MyBEE123HomepageService } from "../../../Component/Api/MyBEE123HomePage/MyBEE123HomepageService";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import { StringDateTimeHelper } from "../../../Component/Model/helpers";
import { SimpleField } from "../../../Component/Common/Components/SimpleField";
import { AppQuill } from "../../../Component/Common/Components/FormValidationInputs/AppQuill";
import { DocumentUploadParameters } from "../../../Component/Model/Dto/Document";
import { DocumentUploadService } from "../../../Component/Api/DocumentUpload/DocumentUploadService";
import { UploadButton } from "../../../Component/Common/Components/UploadButton/UploadButton";
import { InformationDialog } from "../../../Component/Common/Components/InformationDialog";
import { SupportFeedbackTable } from "./Feedback/SupportFeedbackTable";
import { Documents } from "./Documents/Documents";
import { SmallLoading } from "../../../Component/Common/Components/Loading/IntermediateLoading";

interface IProps {
    supportTicketId?: string;
    setSupportTicketId: React.Dispatch<React.SetStateAction<string | undefined>>;
    openModal: boolean;
    handleClose: () => void;
}

export const SupportModal = (props: IProps) => {

    const { control, handleSubmit, formState: { isSubmitting, isValid }, setValue, getValues, trigger } = useForm({
        mode: 'all',
        resolver: yupResolver(supportTicketSchema)
    });

    const [userScorecards, setUserScorecards] = useState<IDropDownOptions[]>([]);
    const [documentsUploaded, setDocumentsUploaded] = useState<Support.TicketDocumentDto[]>([]);
    const [feedback, setFeedback] = useState<Support.TicketFeedbackDtoOut[]>([]);
    const [showTicketAddedDialog, setShowTicketAddedDialog] = useState(false);

    //labeled data
    const [ticketReferenceId, setTicketReferenceId] = useState('');
    const [createdDate, setCreatedDate] = useState('');
    const [supportUser, setSupportUser] = useState('');
    const [status, setStatus] = useState('');
    const [resolvedDate, setResolvedDate] = useState('');
    const [user, setUser] = useState('');
    const [registrationName, setRegistrationName] = useState('');
    const [registrationId, setRegistrationId] = useState('');
    const [statusId, setStatusId] = useState(0);
    const [allowEdit, setAllowEdit] = useState(true);
    const [isReactivating, setIsReactivating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const saveTicket = async (data: Support.DtoIn) => {
        try {
            const result = await SupportRequestService.SaveTicket(data);
            return result.data;
        } catch (error) {
        }
        return '';
    }

    const handleDataSubmit = async (data: FieldValues) => {
        try {
            const ticket = data as Support.DtoIn;
            ticket.Id = props.supportTicketId;
            ticket.RegistrationId = registrationId;
            ticket.StatusId = statusId;
            const ticketId = await saveTicket(ticket);

            if (props.supportTicketId) {
                toast.success('Ticket updated');
                getTicket(props.supportTicketId);
            } else {
                props.setSupportTicketId(ticketId);
                setShowTicketAddedDialog(true);
            }
        } catch (error) {
        }
    }

    const getTicket = async (ticketId?: string) => {
        try {
            const response = await SupportRequestService.GetById(ticketId ?? "0");
            const dataObject = response.data;

            setTicketReferenceId(dataObject.ReferenceId ?? "");
            setCreatedDate(dataObject.CreatedDate ? StringDateTimeHelper(dataObject.CreatedDate.toString()) : StringDateTimeHelper(new Date().toString()));
            setSupportUser(dataObject.SupportUser ?? "Support User not assigned yet.");
            setStatus(ticketStatusList.find(item => item.id === dataObject.StatusId)?.value ?? "");
            setResolvedDate(dataObject.ResolvedDate ? StringDateTimeHelper(dataObject.ResolvedDate.toString()) : "Unspecified");
            setUser(dataObject.CreatedBy);
            setRegistrationName(dataObject.RegistrationName);
            setRegistrationId(dataObject.RegistrationId);
            setStatusId(dataObject.StatusId);
            setDocumentsUploaded(dataObject.Documents ?? []);
            setFeedback(dataObject.FeedbackList ?? []);
            setAllowEdit(dataObject.AllowEdit ?? true);

            setValue("ScorecardId", dataObject.ScorecardId);
            setValue("TypeId", dataObject.TypeId);
            setValue("Description", dataObject.Description);

            if (!ticketId) {
                trigger();
            }

        } catch (error) {
        }
    }

    const getUserScorecards = async () => {
        try {
            const scorecardSearchResult = await MyBEE123HomepageService.getAllUserScorecards();
            const scorecardData: IDropDownOptions[] = scorecardSearchResult.data.map((item) => { return { value: item.Name, id: item.ID } });
            setUserScorecards(scorecardData);
        } catch (error) {
        }
    };

    const uploadFile = async (document: any) => {
        const headers = { 'Content-Type': 'multipart/form-data' };

        let formData = new FormData();
        formData.append(document.name, document);

        try {
            setIsUploading(true);
            const ticket = getValues() as Support.DtoIn;
            ticket.Id = props.supportTicketId;
            ticket.RegistrationId = registrationId;
            ticket.StatusId = statusId;
            const ticketId = await saveTicket(ticket);

            const documentUploadRequest: DocumentUploadParameters = {
                id: ticketId,
                link: "Tickets",
                scorecardId: ticket.ScorecardId,
                documentTypeId: "148"
            };

            await DocumentUploadService.SupportingDocumentsFileUpload(formData, documentUploadRequest, headers);

            if (props.supportTicketId) {
                getTicket(props.supportTicketId);
            } else {
                setShowTicketAddedDialog(true);
                props.setSupportTicketId(ticketId);
            }
            toast.success("File uploaded");
            setIsUploading(false);
        } catch (error) {
        }

    }

    const onFileUpload = (event: any) => {
        if (event.target) {
            const document = event.target.files[0];
            const fileSizeInKB = Math.ceil(document.size / 1024);
            const maxFileSizeInKB = 1024 * 20;
            let fileExt = document.name.split('.').pop();
            const allowedFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'doc', 'docx', 'xls', 'xlsx', 'pdf', 'zip', 'rar', 'csv']
            if (allowedFileTypes.includes(fileExt.toLowerCase())) {
                if (fileSizeInKB > maxFileSizeInKB) {
                    toast.error("File is bigger than 20MB");
                    return;
                }
                else {
                    uploadFile(document);
                    return;
                }
            }
            else {
                toast.error("Unsupported file type. Permitted file types are: .jpeg, .jpg, .png, .gif, .bmp, .tiff, .doc, .docx, .xls, .xlsx, .pdf, .zip, .rar, .csv");
                return;
            }
        }
    }

    const reactivateTicket = async () => {
        try {
            if (props.supportTicketId) {
                setIsReactivating(true);
                await SupportRequestService.ReactivateTicket(props.supportTicketId);
                getTicket(props.supportTicketId);
                toast.info("Ticket is now active")
            }
        } catch (error) {
        }
    }

    const fetchData = async () => {
        setIsLoading(true);
        await getUserScorecards();
        await getTicket(props.supportTicketId);
        setIsLoading(false);
    }

    useEffect(() => {
        fetchData();
    }, [props.supportTicketId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <Dialog disableEnforceFocus open={props.openModal} onClose={props.handleClose} aria-labelledby="form-dialog-title" maxWidth='xl' fullWidth >
                <CustomDialogTitle id="customized-dialog-title" onClose={props.handleClose}>
                    {props.supportTicketId ? "Edit Support Ticket" : "Add Support Ticket"}
                </CustomDialogTitle>
                {isLoading ? <SmallLoading /> :
                    <>
                        <DialogContent>
                            <Grid container spacing={3}>
                                <Grid item container xs={7} component="form" id='SupportTicketForm' onSubmit={handleSubmit(handleDataSubmit)}>
                                    <Grid item xs={8}>
                                        {props.supportTicketId &&
                                            <SimpleField label="Ticket Reference:" value={ticketReferenceId} />
                                        }
                                        <SimpleField label="Created Date:" value={createdDate} />
                                        <SimpleField label="Support User:" value={supportUser} />
                                        <SimpleField label="Status:" value={status} />
                                        <SimpleField label="Resolved Date:" value={resolvedDate} />
                                        <SimpleField label="User:" value={user} />
                                        <SimpleField label="Registration:" value={registrationName} />
                                        <Box mt={2}>
                                            <AppSelectList control={control} name="ScorecardId" label="Scorecard" items={userScorecards} disabled={!allowEdit} />
                                        </Box>
                                        <Box mt={2}>
                                            <AppSelectList control={control} name="TypeId" label="Type" items={ticketTypeList} disabled={!allowEdit} />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MDTypography variant='span'>
                                            Description:
                                        </MDTypography>
                                        <AppQuill control={control} name="Description" placeholder="Please describe your query..." disabled={!allowEdit} />
                                    </Grid>
                                </Grid>
                                <Grid item xs={5}>
                                    <Documents
                                        documentsUploaded={documentsUploaded}
                                        allowEdit={allowEdit}
                                        scorecardId={getValues("ScorecardId")}
                                        afterDelete={() => getTicket(props.supportTicketId)} />
                                </Grid>
                            </Grid>
                            {props.supportTicketId &&
                                <SupportFeedbackTable
                                    allowEdit={allowEdit}
                                    data={feedback}
                                    scorecardId={getValues("ScorecardId")}
                                    supportTicketId={props.supportTicketId}
                                    refreshData={() => getTicket(props.supportTicketId)} />
                            }
                        </DialogContent>
                        <DialogActions>
                            {allowEdit ?
                                <>
                                    <UploadButton
                                        id='upload-file' onChange={onFileUpload} type='file'
                                        accept=".jpeg, .jpg, .png, .gif, .bmp, .tiff, .doc, .docx, .xls, .xlsx, .pdf, .zip, .rar, .csv"
                                        IconView={AttachFileIcon} text='Select file'
                                        color="info"
                                        disabled={!isValid}
                                        loading={isUploading}>
                                    </UploadButton>
                                    <LoadingButton disabled={!isValid} variant='contained' color='primary' type='submit' form='SupportTicketForm' loading={isSubmitting || isUploading} sx={{ ml: 1 }}>
                                        Save
                                    </LoadingButton>
                                </>
                                :
                                <LoadingButton variant='contained' color='primary' sx={{ mr: 1 }} onClick={reactivateTicket} loading={isReactivating}>
                                    Reactivate Ticket
                                </LoadingButton>
                            }
                        </DialogActions>
                    </>
                }
            </Dialog>
            <InformationDialog
                showDialog={showTicketAddedDialog}
                handleAccept={() => {
                    setShowTicketAddedDialog(false);
                }}
                heading={"Ticket Submitted"}
                message={"Thank you for submitting your Support Request. Our Support Team has been notified."}
            />
        </>);
}